import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getSharedReport, type SharedReport as SharedReportData } from '@/api/client'
import { ReportView } from '@/components/interview/ReportView'
import { usePageMeta } from '@/hooks/use-page-meta'
import '@/styles/aura-share.css'
// The shared page renders shell-less but borrows aura-app's wspinner helper.
import '@/styles/aura-app.css'

// Counts-only integrity summary for the anonymous viewer (the backend never
// sends the event timeline on this endpoint). Recovery events are excluded —
// they mirror the flags one-for-one and only the signals themselves count.
const FLAG_LABELS: Record<string, string> = {
  tab_hidden: 'left the tab',
  window_blur: 'window lost focus',
  paste: 'pasted text',
}
const FLAG_ORDER = ['tab_hidden', 'window_blur', 'paste'] as const

/**
 * The public, token-authed report page: the token in the URL is the whole
 * credential, so the page renders for a logged-out stranger — outside the
 * AppShell, like the anonymous invite preview. Read-only by contract: no
 * audio player, no PDF/transcript downloads (ReportView runs with
 * showDownloads={false}), and the candidate's account identity is not in
 * the payload at all.
 */
export function SharedReportPage() {
  usePageMeta('Shared interview report — Aura', { noindex: true })
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<SharedReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    getSharedReport(token)
      .then(d => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setFailed(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [token])

  if (loading) {
    return (
      <div className="shared-page">
        <div className="shared-frame">
          <div className="shared-state" role="status" aria-live="polite">
            <span className="wspinner" aria-hidden="true"></span>
            <span>Loading the shared report…</span>
          </div>
        </div>
      </div>
    )
  }

  // Revoked, mistyped, or a report that never landed are all the same page —
  // the backend answers a uniform 404 and leaks nothing about the session.
  if (failed || !data) {
    return (
      <div className="shared-page">
        <div className="shared-frame">
          <div className="shared-state">
            <h1>This link isn't available</h1>
            <p>The share link is invalid or was revoked by the recruiter.</p>
            <Link to="/" className="btn btn-primary">Go home</Link>
          </div>
        </div>
      </div>
    )
  }

  const counts = data.integrity_counts
  const flaggedSummary = counts
    ? FLAG_ORDER.filter(t => counts[t]).map(t => `${counts[t]}× ${FLAG_LABELS[t]}`).join(' · ')
    : null

  return (
    <div className="shared-page">
      <div className="shared-frame">
        <header className="shared-top">
          <span className="shared-brand">Aura</span>
          {data.shared_by && (
            <span className="shared-by">Shared by <strong>{data.shared_by}</strong></span>
          )}
          <span className="shared-readonly">Read-only view · links to recordings and downloads are not included</span>
        </header>

        {counts !== null && (
          <aside className="shared-integrity">
            {flaggedSummary
              ? <><strong>Integrity signals:</strong> {flaggedSummary}</>
              : <strong>No integrity flags — no tab switches, focus loss, or paste attempts were recorded.</strong>}
            <span className="shared-integrity-note">
              Collected by the browser during the interview — context for your review, not a score.
            </span>
          </aside>
        )}

        <ReportView
          report={data.report}
          completedAt={data.completed_at}
          transcript={data.transcript}
          roleTitle={data.role_title ?? undefined}
          showDownloads={false}
        />
      </div>
    </div>
  )
}
